import { FC, useMemo, useRef } from "react";
import DefaultImg from "../../../../assets/img/default-image.svg";
import Roles from "../../../../models/roles";
import { useAppSelector as useSelector } from "../../../../store";
import { TVacancyDetail } from "../../../../store/vacancy/types";
import { capitalizeName } from "../../../../utils/stringUtils";
import ActionDropdown from "../../../UI/ActionDropdown";
import CustomButton from "../../../UI/Buttons/CustomButton";
import Block from "../../../UI/Icons/Block";
import Candidates from "../../../UI/Icons/Candidates";
import Copy from "../../../UI/Icons/Copy";
import Eye from "../../../UI/Icons/Eye";
import Star from "../../../UI/Icons/Star";
import VerticalDots from "../../../UI/Icons/VerticalDots";
import StarRating from "../../../UI/StarRating";
import VacancyPosition from "../../../UI/VacancyPosition";
import CentreImage from "../../../Vacancy/CentreImage";
import { formatTimeframes } from "../../../Vacancy/timeframeApiHelper";

interface AcceptedCardProps {
  vacancy: TVacancyDetail;
  onViewDetails: () => void;
  showDropdown: boolean;
  setShowDropdown: (show: boolean) => void;
  handleProfileView: () => void;
  handlePreferred: () => void;
  handleDuplicate: () => void;
  handleCancel: () => void;
}

const AcceptedCard: FC<AcceptedCardProps> = ({
  vacancy,
  onViewDetails,
  showDropdown,
  setShowDropdown,
  handleProfileView,
  handlePreferred,
  handleDuplicate,
  handleCancel,
}) => {
  const dropdownRef = useRef<HTMLButtonElement | null>(null);
  const user = useSelector((state) => state.auth.user);

  const isAdmin = useMemo(() => {
    const roleName = user?.role?.name;
    return roleName === Roles.SUPER_ADMIN || roleName === Roles.GENERAL_ADMIN;
  }, [user?.role?.name]);
  const formattedTimeframes = formatTimeframes(vacancy.timeframes);
  const acceptedAt = vacancy.applications.find(
    (app) => app.accepted_at
  )?.accepted_at;

  const dropdownItems = [
    {
      label: "View Candidate Profile",
      icon: <Candidates />,
      onClick: handleProfileView,
    },
    {
      label: !vacancy?.selected_candidate?.is_preferred_candidate
        ? "Mark Candidate as Preferred"
        : "Remove Preferred Status",

      icon: <Star />,
      onClick: handlePreferred,
    },
    {
      label: "Duplicate as New Vacancy",
      icon: <Copy />,
      onClick: handleDuplicate,
    },
    {
      label: "Cancel Vacancy",
      icon: <Block />,
      onClick: handleCancel,
    },
  ];

  return (
    <div className="bg-white p-15px d-flex flex-column flex-lg-row gap-10 align-items-start align-items-lg-center justify-content-between border-bottom border-off-white fs-13 fw-400 text-black">
      <div className="flex-grow-1 w-100 text-break">
        <div className="d-flex gap-20 align-items-center">
          <div className="w-50px h-50px radius-50px">
            {/* <img
              alt="Candidate"
              width="50"
              height="50"
              className="img-responsive rounded-circle object-fit-cover"
              src={vacancy?.centre?.photo}
            /> */}
            <CentreImage vacancy={vacancy} height="50px" width="50px" />
          </div>
          <div className="flex-grow-1 d-flex gap-10 justify-content-between">
            <div className="d-flex flex-column gap-3 text-black">
              <div className="fs-16 fw-700">
                <VacancyPosition
                  positions={vacancy?.vacancy_positions}
                  alternatePosition={vacancy?.alternate_position_legacy || ""}
                />
              </div>
              <div className="fs-13 fw-400">{`${vacancy?.centre?.business_name}, ${vacancy?.centre?.address}`}</div>
            </div>
            <div className="min-w-24px position-relative d-block d-lg-none">
              <CustomButton
                variant={"primary"}
                ref={dropdownRef}
                type="button"
                className="btn-theme bg-white p-0 h-auto"
                onClick={() => setShowDropdown(!showDropdown)}
              >
                <VerticalDots color={!showDropdown ? "#aaaaaa" : "#000000"} />
              </CustomButton>
              <ActionDropdown
                options={dropdownItems}
                isOpen={showDropdown}
                onClose={() => setShowDropdown(false)}
                toggleRef={dropdownRef}
              />
            </div>
          </div>
        </div>
      </div>
      <div className="min-w-250px max-w-250px  mobile-w-100 text-break d-flex gap-20 align-items-center">
        <div className="w-50px h-50px  radius-50px">
          <img
            alt="Candidate"
            width="50px"
            height="50px"
            className="img-responsive rounded-circle object-fit-cover"
            src={vacancy?.selected_candidate?.photo || DefaultImg}
          />
        </div>
        {vacancy?.selected_candidate && (
          <div className="d-flex flex-column gap-3 text-black">
            <div className="fs-16 fw-700">{`${capitalizeName(vacancy.selected_candidate.first_name ?? "")} ${capitalizeName(vacancy.selected_candidate.last_name ?? "")}`}</div>
            <div className="d-flex gap-10 align-items-center flex-wrap">
              <StarRating
                rating={
                  isAdmin
                    ? vacancy?.selected_candidate?.rating?.average_rating || 0
                    : vacancy?.selected_candidate?.rating?.latest_rating ||
                      vacancy?.selected_candidate?.rating?.average_rating ||
                      0
                }
                color={
                  isAdmin || !vacancy?.selected_candidate?.rating?.latest_rating
                    ? "#aaaaaa"
                    : "#51C2B9"
                }
              />
              {/* {!isAdmin &&
                  !vacancy?.selected_candidate?.rating?.latest_rating &&
                  vacancy?.selected_candidate?.rating?.average_rating && (
                  <div className="fs-10 fw-400 text-grey-dark">Average Rating</div>
                  )} */}
              {vacancy.selected_candidate.is_preferred_candidate && (
                <div className="bg-teal-pale border border-teal-dark py-4px px-8px radius-8px text-teal-dark fs-13 lh-14px">
                  Preferred
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      <div className="min-w-250px max-w-250px  mobile-w-100 text-break">
        {formattedTimeframes.map((ftf, i) => (
          <div
            key={i}
            className={`fs-13 ${ftf.alert ? "text-alert fw-700" : "fw-400"}`}
          >
            {ftf.label}
          </div>
        ))}
      </div>
      <div className="min-w-120px">
        <div className="fs-13">{acceptedAt}</div>
      </div>
      <div className="min-w-165px mobile-w-100">
        <CustomButton
          variant={"primary"}
          type="button"
          className="btn-theme bg-off-white text-black w-100"
          onClick={onViewDetails}
        >
          <Eye color="black" />
          View Details
        </CustomButton>
      </div>
      <div className="min-w-24px position-relative d-none d-lg-block">
        <CustomButton
          variant={"primary"}
          ref={dropdownRef}
          type="button"
          className="btn-theme bg-white p-0 h-auto"
          onClick={() => setShowDropdown(!showDropdown)}
        >
          <VerticalDots color={!showDropdown ? "#aaaaaa" : "#000000"} />
        </CustomButton>
        <ActionDropdown
          options={dropdownItems}
          isOpen={showDropdown}
          onClose={() => setShowDropdown(false)}
          toggleRef={dropdownRef}
        />
      </div>
    </div>
  );
};

export default AcceptedCard;
